/// <reference types="react" />
/// <reference types="react-dom" />

import "VSS/LoaderPlugins/Css!DistributedTaskControls/SharedControls/InputControls/Components/RepositoryPickerComponent";
import {
    RepositoryPickerActionCreator
} from "DistributedTaskControls/Actions/RepositoryPickerActionCreator";
import { ActionCreatorManager } from "DistributedTaskControls/Common/Actions/ActionCreatorManager";
import * as Base from "DistributedTaskControls/Common/Components/Base";
import { VariableParameterRegex } from "DistributedTaskControls/Common/RegexConstants";
import { StoreManager } from "DistributedTaskControls/Common/Stores/StoreManager";
import * as DTCResources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";
import { ServiceEndpointRequest } from "TFS/ServiceEndpoint/Contracts";
import {
    FilePathInputComponent
} from "DistributedTaskControls/SharedControls/InputControls/Components/FilePathInputComponent";
import {
    RepositorySelectorComponent
} from "DistributedTaskControls/SharedControls/InputControls/Components/RepositorySelectorComponent";
import { RepositoryPickerSource } from "DistributedTaskControls/Sources/RepositoryPickerSource";
import {
    IRepositoryPickerState, RepositoryPickerStore
} from "DistributedTaskControls/Stores/RepositoryPickerStore";
import { Dialog, DialogType } from "OfficeFabric/Dialog";
import * as React from "react";
import * as Utils_String from "VSS/Utils/String";
import * as Q from "q";

export interface IRepositoryPickerProps extends Base.IProps {
  value: string;
  connectionId: string;
  disabled: boolean;
  required: boolean;
  getRepositoryByNameDataSource: string;
  errorMessage: string;
  instanceId: string;
  allowVariables: boolean;
  initialValues: string[];
  ariaLabelledBy : string;
  getInitialListEndpointRequest: () => IPromise<ServiceEndpointRequest>;
  getSearchEndpointRequest(searchKey: string): ServiceEndpointRequest;
  onRepositoryChanged: (repository: string, repositoryKey: string, skipFetchingDependency: boolean) => void;
}

export class RepositoryPickerComponent extends Base.Component<IRepositoryPickerProps, IRepositoryPickerState> {

  constructor(props: IRepositoryPickerProps) {
    super(props);
    const errorMessage = this.props.value ? Utils_String.empty : this.props.errorMessage;
    this._store = StoreManager.CreateStore<RepositoryPickerStore, IRepositoryPickerState>
                      (RepositoryPickerStore, this.props.instanceId, {value: this.props.value, errorMessage: errorMessage, showRepositorySelectorDialog: false, initialValues: this.props.initialValues});
    this._repositoryPickerActionCreator = ActionCreatorManager.CreateActionCreator<RepositoryPickerActionCreator, RepositoryPickerStore>
                                                      (RepositoryPickerActionCreator, this.props.instanceId, this._store);
    this._repositoryPickerSource = RepositoryPickerSource.instance();
  }

  public componentWillMount() {
    this._store.addChangedListener(this._onStoreChange);
    this.setState(this._store.getState());
  }

  public componentWillUnmount(): void {
    this._store.removeChangedListener(this._onStoreChange);
  }

  public render(): JSX.Element {
    let dialog: JSX.Element;

    if (this.state.showRepositorySelectorDialog) {
      dialog = <Dialog
        modalProps={{
          className: "repository-selector-dialog",
          containerClassName: "repository-selector-dialog-container",
          isBlocking: true
        }}
        hidden={!this.state.showRepositorySelectorDialog}
        dialogContentProps={{
          type: DialogType.close
        }}
        title={DTCResources.SelectRepositoryTitle}
        onDismiss={this._onCloseRepositoryBrowser}
      >
        <RepositorySelectorComponent
          value={this.state.value}
          connectionId={this.props.connectionId}
          initialValues={this.props.initialValues}
          onRepositorySelected={this._onRepositoryChanged}
          onCloseRepositoryBrowser={this._onCloseRepositoryBrowser}
          getInitialListEndpointRequest={this.props.getInitialListEndpointRequest}
          getSearchEndpointRequest={this.props.getSearchEndpointRequest}
          instanceId={Utils_String.generateUID()}
          isVariable={this.isVariable}
        />
      </Dialog>;
    } else {
      dialog = null;
    }

    return (
      <div>
        <FilePathInputComponent
          disabled={this.props.disabled}
          readOnly={false}
          ariaLabelledBy={this.props.ariaLabelledBy}
          isFileSystemBrowsable={() => true}
          onValueChanged={this._onValueChanged}
          filePathProviderDelegate={this._onShowRepositoryBrowser}
          value={this.state.value}
          required={this.props.required}
          getErrorMessage={this._getErrorMessage}
          deferredValidationTime={100} // for reducing the delay with displaying error message
        />
        {dialog}
      </div>
    );
  }

  public isVariable(value: string): boolean {
    return VariableParameterRegex.test(value);
  }

  private _onStoreChange = (): void => {
    this.setState(this._store.getState());
  }

  private _onValueChanged = (value: string): void => {
    this._repositoryPickerActionCreator.updateValue(value);
    if (!value || Utils_String.ignoreCaseComparer(value.trim(), Utils_String.empty) === 0) {
      this.props.onRepositoryChanged(Utils_String.empty, Utils_String.empty, true);
    } else if (!!this.props.allowVariables && this.isVariable(value)) {
       this.props.onRepositoryChanged(value, value, false);
    } else {
      this._repositoryPickerSource.validateRepositoryName(value, this.props.connectionId, this.props.getRepositoryByNameDataSource)
      .then((errorMessage) => {
        if (value === this.state.value) {
          if (Utils_String.ignoreCaseComparer(errorMessage, Utils_String.empty) === 0) {
            this.props.onRepositoryChanged(value, value, false);
          } else {
            this.props.onRepositoryChanged(Utils_String.empty, Utils_String.empty, true);
          }
        }
      },    () => {
              if (value === this.state.value) {
                this.props.onRepositoryChanged(Utils_String.empty, Utils_String.empty, true);
              }
      });
    }
  }

  private _onRepositoryChanged = (selectedItem: string) => {
    this._repositoryPickerActionCreator.updateValue(selectedItem);
    this.props.onRepositoryChanged(selectedItem, selectedItem, false);
  }

  private _onShowRepositoryBrowser = () => {
    this._repositoryPickerActionCreator.updateShowRepositorySelectorDialog(true);
  }

  private _onCloseRepositoryBrowser = () => {
    this._repositoryPickerActionCreator.updateShowRepositorySelectorDialog(false);
  }

  private _getErrorMessage = (value: string): string | IPromise<string> => {
    if (Utils_String.ignoreCaseComparer(value.trim(), Utils_String.empty) === 0) {
      return Q.resolve(this.props.errorMessage);
    }
    if (!!this.props.allowVariables && this.isVariable(value)) {
      return Q.resolve(Utils_String.empty);
    }
    return this._repositoryPickerSource.validateRepositoryName(value, this.props.connectionId, this.props.getRepositoryByNameDataSource);
  }

  private _repositoryPickerActionCreator: RepositoryPickerActionCreator;
  private _store: RepositoryPickerStore;
  private _repositoryPickerSource: RepositoryPickerSource;
}
